import React, { useState } from "react";
import "./SearchForm.css";
import AddressAutocomplete from "../AddressAutocomplete/AddressAutocomplete";

function SearchForm({ onSearch, isLoading }) {
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [reportId, setReportId] = useState("");
  // The property ZIP box serves both searches: a Report ID always needs it, and
  // an address uses it when the ZIP is not in the address itself.
  const [propertyZip, setPropertyZip] = useState("");
  const [reportZipError, setReportZipError] = useState(false);
  const [isFieldEmpty, setIsFieldEmpty] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState(false);
  
  // Internal state for extracted address components
  const [streetNumber, setStreetNumber] = useState("");

  // Handle address validation from AddressAutocomplete
  const handleAddressValidation = (addressFound, hasZipCode, addressSelected) => {
    // Only show error on blur (when addressFound is false and onAddressValidation is called from blur)
    // This prevents real-time validation errors while typing
    if (!addressFound && !hasZipCode && !/^\d{5}$/.test(propertyZip) && address.length > 2) {
      setAddressValidationError(true);
    } else if (addressFound) {
      // Clear error when suggestions are found
      setAddressValidationError(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!address && !reportId) {
      setIsFieldEmpty(true);
      return;
    }

    if (reportId) {
      if (!/^\d{5}$/.test(propertyZip)) {
        setReportZipError(true);
        return;
      }
      setIsFieldEmpty(false);
      onSearch("", propertyZip, reportId);
      return;
    }

    // The ZIP comes from the selected suggestion, a ZIP typed in the address, or
    // the property ZIP box.
    const addressZip =
      zipCode ||
      (address.match(/\b(\d{5})(?:-\d{4})?\b/) || [])[1] ||
      (/^\d{5}$/.test(propertyZip) ? propertyZip : "");
    if (!addressZip) {
      setAddressValidationError(true);
      return;
    }

    setAddressValidationError(false);
    setIsFieldEmpty(false);
    // Only the street part goes to the API ("16 Clarence Ct"), never the city or
    // state: the search matches the street number and then the street name.
    const street = address.split(",")[0].trim();
    onSearch(/^\d/.test(street) ? street : streetNumber, addressZip, "");
  };

  return (
    <>
      <section className="search-form">
        <h2>Search Reports</h2>
        <form onSubmit={handleSubmit}>
          <div className="search-fields">
            <div className="field">
              <label htmlFor="address">Address</label>
              <AddressAutocomplete
                id="address"
                value={address}
                onChange={(value) => {
                  setAddress(value);
                  setIsFieldEmpty(false);
                  setAddressValidationError(false); // Clear validation error when address changes
                  
                  // Clear ZIP code if address is deleted
                  if (!value) {
                    setZipCode("");
                  }
                }}
                onZipCodeChange={(value) => {
                  setZipCode(value);
                  setIsFieldEmpty(false);
                }}
                onStreetNumberChange={(value) => {
                  setStreetNumber(value);
                }}
                onAddressValidation={handleAddressValidation}
                placeholder="Enter address..."
              />
              <small className="field-instruction">
                Address: Number, Street, City, State Zip (e.g., 123 Main St, San Francisco, CA 94105)
              </small>
            </div>
            <div className="or-separator">OR</div>
            <div className="field">
              <label htmlFor="reportId">Report ID</label>
              <input
                type="text"
                id="reportId"
                value={reportId}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow alphanumeric characters up to 6 characters
                  if (value === "" || /^[a-zA-Z0-9]{0,6}$/.test(value)) {
                    setReportId(value);
                    setIsFieldEmpty(false);
                  }
                }}
                placeholder="Enter Report ID"
              />
            </div>
            <div className="field zip-field">
              <label htmlFor="reportZip">Property ZIP code</label>
              <input
                type="text"
                id="reportZip"
                inputMode="numeric"
                autoComplete="postal-code"
                value={propertyZip}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d{0,5}$/.test(value)) {
                    setPropertyZip(value);
                    setReportZipError(false);
                    setAddressValidationError(false);
                  }
                }}
                placeholder="ZIP code"
              />
            </div>
            <button type="submit" disabled={isLoading}>
              {isLoading ? "Searching..." : "Search"}
            </button>
          </div>
          {/* Error messages below the row */}
          {(isFieldEmpty || addressValidationError || reportZipError) && (
            <div className="error-messages">
              {isFieldEmpty && (
                <p className="error-message">
                  Atleast one field is required to search reports.
                </p>
              )}
              {addressValidationError && (
                <p className="error-message">
                  Please enter the property ZIP code, or select your address from the suggestions.
                </p>
              )}
              {reportZipError && (
                <p className="error-message">
                  Please enter the 5-digit property ZIP code for this Report ID.
                </p>
              )}
            </div>
          )}
        </form>
      </section>
      {isLoading && (
          <div className="loader-container">
            <div className="spinner" />
            <p>Fetching results...</p>
          </div>
        )}
    </>
  );
}

export default SearchForm;
